import React, { createContext, Reducer, useReducer } from 'react';
import { produce } from 'immer';
import { entries } from '../../../util/entries';
import { LocalIdentities, LocalIdentity } from '../../types';
import { LocalSystems } from '../../localSystems';

export type IdentitiesState = Partial<LocalIdentities>;
export type Actions = LinkIdentity | UnlinkIdentity | Initialize;

export type LinkIdentity = {
	type: 'linkIdentity';
	systemId: LocalSystems;
	identity: LocalIdentity;
}

export type UnlinkIdentity = {
	type: 'unlinkIdentity';
	systemId: LocalSystems;
	localUserId: string;
}

export type Initialize = {
	type: 'initialize';
	identities: LocalIdentities;
}

const Dispatch = createContext<React.Dispatch<Actions>>(() => {});
const State = createContext<IdentitiesState>({
	ip: [],
	mascus: [],
	rba: []
});

const reducer: Reducer<IdentitiesState, Actions> = (state, action) => {
	switch (action.type) {
		case 'linkIdentity':
			return produce(state, (systems) => {
				const identities = systems[action.systemId] || [];

				identities.push(action.identity);

				systems[action.systemId] = identities;
			});

		case 'unlinkIdentity':
			return produce(state, (systems) => {
				const identities = systems[action.systemId];

				if (!identities) return;

				systems[action.systemId] = identities.filter(({ id }) => id !== action.localUserId);

				if (!systems[action.systemId]!.length) delete systems[action.systemId];
			});

		case 'initialize':
			for (const [systemId, identities] of entries(action.identities)) {
				if (!identities.length) delete action.identities[systemId];
			}

			return action.identities;

		default:
			throw new Error('Invalid action type in Identities reducer');
	}
}

function Provider({ children }: { children: JSX.Element[] | JSX.Element }) {
	const [state, dispatch] = useReducer(reducer, {
		ip: [],
		mascus: [],
		rba: []
	});

	// The state and the dispatch function should be provided seperately so that components that are interested
	// only in dispatching events don't need to use the state and thus re-render when the state changes
	// See here - https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
	return (
		<State.Provider value={state}>
			<Dispatch.Provider value={dispatch}>{children}</Dispatch.Provider>
		</State.Provider>
	)
}

export const Identities = {
	State,
	Dispatch,
	Provider
};